import api from "../api";
import type { AvatarPerson } from "components/common/avatarModel";

export type SecureFileDeleteReason = "" | "manual" | "expired";

export interface SecureFile {
  id: number;
  file_name: string;
  content_type: string;
  size: number;
  message: string;
  is_silent: boolean;
  is_deleted: boolean;
  deleted_at: string | null;
  delete_reason: SecureFileDeleteReason;
  delete_reason_display: string;
  deleted_by_name: string;
  purges_on: string | null;
  can_restore: boolean;
  /** Deleted files: where Restore puts the file back, or why it cannot yet. */
  restore_to: SecureRestoreTarget | null;
  created_at: string;
  client_id: number | null;
  client_name: string | null;
  /** No project: the file is in Individual Files. */
  project_id: number | null;
  collection_id: number | null;
  collection_name: string | null;
  project_name: string | null;
  project_status: SecureProjectStatus | null;
  is_confidential: boolean;
  /** Not opened (previewed or downloaded) by the viewer yet. */
  is_unread: boolean;
  /** The latest download and how many others downloaded; null reads "Not yet". */
  downloaded_by: SecureFileDownloadedBy | null;
  uploaded_by_id: number | null;
  uploaded_by_name: string;
  can_delete: boolean;
  /** Who added the file; null when that person was removed. */
  added_by: AvatarPerson | null;
  /** The people the file is meant for. */
  shared_with: AvatarPerson[];
}

export interface SecureRestoreTarget {
  /** "Individual Files", the project, or "project / collection". */
  label: string;
  /** Back in Individual Files: every Secure Exchange user of the client sees it. */
  individual: boolean;
  refusal: string | null;
  /** A first-version file back in Individual Files: only its sender and the
   * people it is shared with see it there. */
  shared_only: boolean;
}

export interface SecureFileDownloadedBy {
  name: string;
  at: string;
  others: number;
}

export type SecureFileEventKind =
  | "uploaded"
  | "previewed"
  | "downloaded"
  | "moved"
  | "deleted"
  | "restored";

export interface SecureFileEvent {
  kind: SecureFileEventKind;
  at: string;
  by: string;
  moved_from: string;
  moved_to: string;
}

/** A client user as staff see them on a project. */
export interface SecurePerson extends AvatarPerson {
  email: string;
  /** Holds "Can Access Confidential Information". */
  confidential: boolean;
}

export type SecureProjectStatus = "open" | "closed";

export interface SecureProject {
  id: number;
  client_id: number;
  client_name: string;
  name: string;
  description: string;
  status: SecureProjectStatus;
  created_at: string;
  closed_at: string | null;
  /** Live files the viewer sees in it. */
  files: number;
}

export interface SecureAccessChange extends SecurePerson {
  /** Files gained (adding) or lost (removing). */
  files: number;
  /** Removing only: how many of the lost files were shared with them. */
  shared?: number;
}

export interface SecureAccessPreview {
  adding: SecureAccessChange[];
  removing: SecureAccessChange[];
}

/** A project on its client's page. */
export interface SecureProjectRow extends SecureProject {
  /** The client's staff who work its Secure Exchange, then the members. */
  contributors: AvatarPerson[];
  latest_activity: string | null;
}

export type SecureProjectColumn = "name" | "status" | "files" | "latest_activity";

export interface SecureProjectListRequest {
  client_id: number;
  status?: SecureProjectStatus;
  search?: string;
  /** A column, "-" first when descending; left out, open projects come first. */
  ordering?: string;
  page?: number;
  page_size?: number;
}

export interface SecureProjectListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: SecureProjectRow[];
}

/** A collection on its project's page. */
export interface SecureCollectionRow {
  id: number;
  name: string;
  added_by: AvatarPerson | null;
  /** Everyone the files the viewer sees in it are shared with. */
  shared_with: AvatarPerson[];
  /** Holds a file the viewer has not opened. */
  has_unread: boolean;
  /** Files the viewer sees in it. */
  files: number;
  created_at: string;
}

export interface SecureProjectItemsRequest {
  id: number;
  search?: string;
  page?: number;
  page_size?: number;
}

/** A page of a project: its collections first, then its files. */
export interface SecureProjectItemsResponse {
  count: number;
  collections: SecureCollectionRow[];
  files: SecureFile[];
}

export interface CreateSecureProjectRequest {
  client_id: number;
  name: string;
  description: string;
  member_ids: number[];
}

export interface SecureFileListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: SecureFile[];
}

/** "deleted": Deleted Files (Manage); anything else, the live files. */
export type SecureFileTab = "deleted" | "live";

export interface SecureFileListRequest {
  tab?: SecureFileTab;
  search?: string;
  client?: number | null;
  /** "individual": only the files in no project. */
  location?: "individual";
  /** Only the files of this collection. */
  collection?: number;
  sort_by?: string | null;
  sort_order?: "asc" | "desc" | null;
  page?: number;
  page_size?: number;
}

/** Where an upload goes: Individual Files when neither id is set. */
export interface SecureUploadLocation {
  client_id?: number | null;
  project_id?: number | null;
  collection_id?: number | null;
}

export interface SecureClientOption {
  id: number;
  company_name: string;
  company_code: string;
}

/** The columns the Client List sorts by; a leading "-" sorts descending. */
export type SecureClientColumn =
  | "name"
  | "open_projects"
  | "new_files"
  | "contributors"
  | "latest_activity";

export interface SecureClientRow {
  id: number;
  name: string;
  code: string;
  /** Open projects the viewer sees there. */
  open_projects: number;
  /** Files the viewer sees there and has not opened. */
  new_files: number;
  contributors: number;
  latest_activity: string | null;
}

export interface SecureClientListRequest {
  search?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
}

export interface SecureClientListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: SecureClientRow[];
  /** The page header: listed clients and the viewer's own open projects. */
  totals: { clients: number; open_projects: number };
}

/** A client page's header. */
export interface SecureClientSummary {
  id: number;
  name: string;
  code: string;
  open_projects: number;
  files: number;
  contributors: number;
}

export interface SecureCollectionSummary {
  id: number;
  name: string;
  project_id: number;
  project_name: string;
  project_status: SecureProjectStatus;
  client_id: number;
  client_name: string;
  files: number;
}

export interface PresignResponse {
  upload_url: string;
  s3_key: string;
  s3_bucket: string;
  file_name: string;
  headers: Record<string, string>;
}

export interface FinalizeRequest extends SecureUploadLocation {
  s3_key: string;
  /** Everyone the file is meant for; each will see it there. */
  shared_with: number[];
  is_confidential: boolean;
  file_name: string;
  content_type: string;
  message?: string;
  is_silent?: boolean;
}

/** What moving a file to a project would change. */
export interface SecureMoveCheck {
  from: string;
  to: string;
  /** Not on the target project: the move needs them added first. */
  losing: SecurePerson[];
  /** Will see the file there and do not see it now. */
  gaining: SecurePerson[];
}

export interface SecureMoveRequest {
  id: number;
  project_id: number;
  collection_id?: number | null;
  add_members?: boolean;
}

export interface SecureFileDownload {
  by: string;
  at: string;
}

const BASE = "/secure-uploads";

export const secureUploadsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getSecureFiles: builder.query<SecureFileListResponse, SecureFileListRequest>({
      query: ({
        tab,
        search,
        client,
        location,
        collection,
        sort_by,
        sort_order,
        page,
        page_size,
      }) => ({
        url: `${BASE}/`,
        params: {
          tab: tab || "live",
          search: search || undefined,
          client: client || undefined,
          location: location || undefined,
          collection: collection || undefined,
          sort_by: sort_by || undefined,
          sort_order: sort_by ? sort_order || "asc" : undefined,
          page: page ?? 1,
          page_size: page_size ?? 25,
        },
      }),
      providesTags: ["SecureUploads"],
    }),

    getSecureRecipients: builder.query<
      SecurePerson[],
      SecureUploadLocation & { confidential?: boolean }
    >({
      query: ({ client_id, project_id, collection_id, confidential }) => ({
        url: `${BASE}/recipients/`,
        params: {
          client_id: client_id || undefined,
          project_id: project_id || undefined,
          collection_id: collection_id || undefined,
          confidential: confidential ? 1 : undefined,
        },
      }),
    }),
    getSecureProjectCollections: builder.query<
      { id: number; name: string }[],
      number
    >({
      query: (projectId) => ({
        url: `${BASE}/projects/${projectId}/collections/`,
      }),
      providesTags: ["SecureProjects"],
    }),
    getSecureMoveCheck: builder.query<
      SecureMoveCheck,
      Omit<SecureMoveRequest, "add_members">
    >({
      query: ({ id, project_id, collection_id }) => ({
        url: `${BASE}/${id}/move/`,
        params: { project_id, collection_id: collection_id || undefined },
      }),
      keepUnusedDataFor: 0,
    }),
    moveSecureFile: builder.mutation<SecureFile, SecureMoveRequest>({
      query: ({ id, ...body }) => ({
        url: `${BASE}/${id}/move/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["SecureUploads", "SecureProjects"],
    }),
    getSecureFileDownloads: builder.query<SecureFileDownload[], number>({
      query: (id) => ({ url: `${BASE}/${id}/downloads/` }),
      providesTags: ["SecureUploads"],
    }),

    getSecureClients: builder.query<SecureClientOption[], void>({
      query: () => ({ url: `${BASE}/clients/options/` }),
    }),
    getSecureClientList: builder.query<
      SecureClientListResponse,
      SecureClientListRequest
    >({
      query: (params) => ({ url: `${BASE}/clients/`, params }),
      providesTags: ["SecureUploads", "SecureProjects"],
    }),
    getSecureClient: builder.query<SecureClientSummary, number>({
      query: (id) => ({ url: `${BASE}/clients/${id}/` }),
      providesTags: ["SecureUploads", "SecureProjects"],
    }),
    getSecureCollection: builder.query<SecureCollectionSummary, number>({
      query: (id) => ({ url: `${BASE}/collections/${id}/` }),
      providesTags: ["SecureUploads", "SecureProjects"],
    }),

    getSecureUnreadCount: builder.query<{ unread: number }, void>({
      query: () => ({ url: `${BASE}/unread-count/` }),
      providesTags: ["SecureUploadsUnread"],
    }),

    createSecureProject: builder.mutation<
      SecureProject,
      CreateSecureProjectRequest
    >({
      query: (body) => ({ url: `${BASE}/projects/`, method: "POST", body }),
      invalidatesTags: ["SecureProjects"],
    }),

    getSecureProjects: builder.query<
      SecureProjectListResponse,
      SecureProjectListRequest
    >({
      query: (params) => ({ url: `${BASE}/projects/`, params }),
      providesTags: ["SecureProjects", "SecureUploads"],
    }),
    getSecureProjectItems: builder.query<
      SecureProjectItemsResponse,
      SecureProjectItemsRequest
    >({
      query: ({ id, ...params }) => ({
        url: `${BASE}/projects/${id}/items/`,
        params,
      }),
      providesTags: ["SecureProjects", "SecureUploads"],
    }),
    createSecureCollection: builder.mutation<
      SecureCollectionSummary,
      { projectId: number; name: string }
    >({
      query: ({ projectId, name }) => ({
        url: `${BASE}/projects/${projectId}/collections/`,
        method: "POST",
        body: { name },
      }),
      invalidatesTags: ["SecureProjects", "SecureUploads"],
    }),
    renameSecureCollection: builder.mutation<
      SecureCollectionSummary,
      { id: number; name: string }
    >({
      query: ({ id, name }) => ({
        url: `${BASE}/collections/${id}/`,
        method: "PATCH",
        body: { name },
      }),
      invalidatesTags: ["SecureProjects", "SecureUploads"],
    }),
    deleteSecureCollection: builder.mutation<{ deleted_files: number }, number>(
      {
        query: (id) => ({ url: `${BASE}/collections/${id}/`, method: "DELETE" }),
        invalidatesTags: ["SecureProjects", "SecureUploads"],
      },
    ),
    getSecureProject: builder.query<SecureProject, number>({
      query: (id) => ({ url: `${BASE}/projects/${id}/` }),
      providesTags: (_r, _e, id) => [{ type: "SecureProjects", id }],
    }),

    updateSecureProject: builder.mutation<
      SecureProject,
      { id: number; name?: string; description?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `${BASE}/projects/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["SecureProjects"],
    }),

    closeSecureProject: builder.mutation<SecureProject, number>({
      query: (id) => ({ url: `${BASE}/projects/${id}/close/`, method: "POST" }),
      invalidatesTags: ["SecureProjects"],
    }),

    reopenSecureProject: builder.mutation<SecureProject, number>({
      query: (id) => ({ url: `${BASE}/projects/${id}/reopen/`, method: "POST" }),
      invalidatesTags: ["SecureProjects"],
    }),

    deleteSecureProject: builder.mutation<{ deleted_files: number }, number>({
      query: (id) => ({ url: `${BASE}/projects/${id}/`, method: "DELETE" }),
      invalidatesTags: ["SecureProjects", "SecureUploads"],
    }),

    getSecureProjectCandidates: builder.query<SecurePerson[], number>({
      query: (clientId) => ({
        url: `${BASE}/projects/candidates/`,
        params: { client_id: clientId },
      }),
    }),

    getSecureProjectMembers: builder.query<SecurePerson[], number>({
      query: (id) => ({ url: `${BASE}/projects/${id}/members/` }),
      providesTags: (_r, _e, id) => [{ type: "SecureProjects", id }],
    }),

    setSecureProjectMembers: builder.mutation<
      SecurePerson[],
      { id: number; member_ids: number[] }
    >({
      query: ({ id, member_ids }) => ({
        url: `${BASE}/projects/${id}/members/`,
        method: "PUT",
        body: { member_ids },
      }),
      invalidatesTags: ["SecureProjects", "SecureUploads"],
    }),

    // A GET, so it also opens in Client View, where the save is refused.
    getSecureProjectAccessPreview: builder.query<
      SecureAccessPreview,
      { id: number; member_ids: number[] }
    >({
      query: ({ id, member_ids }) => ({
        url: `${BASE}/projects/${id}/members/preview/`,
        params: { member_ids: member_ids.join(",") },
      }),
    }),

    // Staff only (404 for a client user): a move names projects a client user
    // may not be on.
    getSecureFileHistory: builder.query<SecureFileEvent[], number>({
      query: (id) => ({ url: `${BASE}/${id}/history/` }),
      providesTags: ["SecureUploads"],
    }),

    presignSecureUpload: builder.mutation<
      PresignResponse,
      // Staff name the client; a client user always uploads to their own.
      SecureUploadLocation & { file_name: string; content_type: string }
    >({
      query: (body) => ({
        url: `${BASE}/upload/presign/`,
        method: "POST",
        body,
      }),
    }),

    finalizeSecureUpload: builder.mutation<
      SecureFile & { emails_sent: number },
      FinalizeRequest
    >({
      query: (body) => ({
        url: `${BASE}/upload/finalize/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["SecureUploads", "SecureUploadsUnread"],
    }),

    getSecureFileAccessUrl: builder.mutation<
      { url: string; file_name: string },
      { id: number; mode?: "preview" }
    >({
      query: ({ id, mode }) => ({
        url: `${BASE}/${id}/access/`,
        method: "POST",
        body: mode ? { mode } : {},
      }),
      // A preview opens the file too: its dot and the badge go.
      invalidatesTags: ["SecureUploads", "SecureUploadsUnread"],
    }),

    deleteSecureFile: builder.mutation<void, number>({
      query: (id) => ({
        url: `${BASE}/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["SecureUploads", "SecureUploadsUnread"],
    }),

    restoreSecureFile: builder.mutation<SecureFile, number>({
      query: (id) => ({
        url: `${BASE}/${id}/restore/`,
        method: "POST",
      }),
      invalidatesTags: ["SecureUploads", "SecureUploadsUnread", "SecureProjects"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetSecureFilesQuery,
  useGetSecureRecipientsQuery,
  useGetSecureClientsQuery,
  useGetSecureProjectCollectionsQuery,
  useGetSecureFileDownloadsQuery,
  useGetSecureMoveCheckQuery,
  useMoveSecureFileMutation,
  useGetSecureClientListQuery,
  useGetSecureClientQuery,
  useGetSecureCollectionQuery,
  useGetSecureUnreadCountQuery,
  useGetSecureFileHistoryQuery,
  useCreateSecureProjectMutation,
  useGetSecureProjectsQuery,
  useGetSecureProjectItemsQuery,
  useCreateSecureCollectionMutation,
  useRenameSecureCollectionMutation,
  useDeleteSecureCollectionMutation,
  useGetSecureProjectQuery,
  useUpdateSecureProjectMutation,
  useCloseSecureProjectMutation,
  useReopenSecureProjectMutation,
  useDeleteSecureProjectMutation,
  useGetSecureProjectCandidatesQuery,
  useGetSecureProjectMembersQuery,
  useSetSecureProjectMembersMutation,
  useLazyGetSecureProjectAccessPreviewQuery,
  usePresignSecureUploadMutation,
  useFinalizeSecureUploadMutation,
  useGetSecureFileAccessUrlMutation,
  useDeleteSecureFileMutation,
  useRestoreSecureFileMutation,
} = secureUploadsApi;
